import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerTopHeader from '../components/ManagerTopHeader';

import { API_BASE } from '../../config/runtime';

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/* ─── Minimal keyframes only (not expressible in Tailwind) ──────────────── */
if (typeof document !== 'undefined' && !document.getElementById('ms-keyframes')) {
  const style = document.createElement('style');
  style.id = 'ms-keyframes';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

    .ms-fade-up {
      opacity: 0;
      transform: translateY(20px);
      animation: msFadeUp 0.45s ease forwards;
    }
    @keyframes msFadeUp { to { opacity: 1; transform: translateY(0); } }

    .ms-spin { animation: msSpin 0.8s linear infinite; }
    @keyframes msSpin { to { transform: rotate(360deg); } }

    .ms-card:hover .ms-card-img { transform: scale(1.06); }

    @keyframes shimmer {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    .ms-shimmer {
      background: linear-gradient(90deg,#F1F5F9 25%,#E8EDF2 50%,#F1F5F9 75%);
      background-size: 200% 100%;
      animation: shimmer 1.4s infinite;
    }

    @keyframes msFadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes msSlideUp {
      from { opacity: 0; transform: translateY(60px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes msZoom {
      from { opacity: 0; transform: scale(.92); }
      to   { opacity: 1; transform: scale(1); }
    }
    .ms-modal-overlay { animation: msFadeIn .2s ease; }
    .ms-modal-mobile  { animation: msSlideUp .28s cubic-bezier(.34,1.56,.64,1); }
    @media (min-width: 640px) {
      .ms-modal-desktop { animation: msZoom .25s cubic-bezier(.34,1.56,.64,1); }
    }

    .ms-card-img { transition: transform .5s ease; }
  `;
  document.head.appendChild(style);
}

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
  </svg>
);
const IconX = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
);
const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
);
const IconClock = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IconTag = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);
const IconChevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const IconImage = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);

/* ─── Service Card ───────────────────────────────────────────────────────── */
const ServiceCard = ({ service, onSelect, delay = 0 }) => (
  <div
    className="ms-card ms-fade-up bg-white border border-slate-200 rounded-[20px] overflow-hidden flex flex-col cursor-default transition-all duration-200 hover:shadow-2xl hover:-translate-y-1"
    style={{ animationDelay: `${delay}ms` }}
    onClick={() => onSelect(service)}
  >
    {/* Image */}
    <div className="relative overflow-hidden bg-slate-100 h-[130px] sm:h-[180px]">
      {service.image
        ? <img src={service.image} className="ms-card-img w-full h-full object-cover" alt={service.name} />
        : (
          <div className="flex items-center justify-center h-full">
            <IconImage size={32} />
          </div>
        )
      }
      {/* Duration badge — only shown when duration is set */}
      {service.duration_minutes && (
        <div
          className="absolute top-2 right-2 flex items-center gap-1 bg-slate-900/70 backdrop-blur-sm text-white rounded-lg px-2 py-[3px] text-[10px] font-bold"
          style={{ fontFamily: 'Sora, sans-serif' }}
        >
          <IconClock /> {service.duration_minutes}
        </div>
      )}
    </div>

    {/* Body */}
    <div className="p-3 pb-[14px] flex-1 flex flex-col justify-between gap-2">
      <p
        className="font-bold text-xs text-slate-900 leading-snug m-0 overflow-hidden"
        style={{
          fontFamily: 'Sora, sans-serif',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {service.name}
      </p>

      <div className="flex items-center justify-between gap-1 flex-wrap">
        <span className="font-extrabold text-[13px] text-blue-600" style={{ fontFamily: 'Sora, sans-serif' }}>
          ₱{Number(service.price).toLocaleString()}
        </span>
        <button
          className="inline-flex items-center justify-center font-bold tracking-widest uppercase border-none cursor-pointer transition-all duration-200 active:scale-95 bg-slate-900 text-white text-[9px] px-[10px] py-[5px] rounded-lg hover:bg-blue-600"
          style={{ fontFamily: 'Sora, sans-serif' }}
          onClick={(e) => { e.stopPropagation(); onSelect(service); }}
        >
          Details
        </button>
      </div>
    </div>
  </div>
);

/* ─── Service Details Modal ──────────────────────────────────────────────── */
const ServiceModal = ({ service, onClose }) => {
  if (!service) return null;
  return (
    <div
      className="ms-modal-overlay fixed inset-0 z-[200] bg-slate-900/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="ms-modal-mobile sm:ms-modal-desktop bg-white rounded-t-[28px] sm:rounded-[28px] w-full sm:max-w-[520px] max-h-[92vh] sm:max-h-[90vh] overflow-y-auto shadow-[0_40px_80px_-12px_rgba(15,23,42,0.35)] scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-slate-300"
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-3 pb-0">
          <div className="w-9 h-1 rounded-sm bg-slate-200" />
        </div>

        {/* Image header */}
        <div className="relative h-[220px]">
          {service.image
            ? <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
            : (
              <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                <IconImage size={48} />
              </div>
            )
          }
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 to-transparent" />

          {/* Close btn */}
          <button
            onClick={onClose}
            className="absolute top-[14px] right-[14px] w-[34px] h-[34px] rounded-full bg-white/20 backdrop-blur-sm border border-white/25 text-white flex items-center justify-center cursor-pointer"
          >
            <IconClose />
          </button>

          {/* Category + title */}
          <div className="absolute bottom-0 left-0 right-0 px-5 pb-5">
            <span
              className="inline-flex items-center gap-1.5 bg-blue-600/85 text-white text-[11px] font-bold tracking-[0.06em] uppercase px-3 py-1 rounded-full mb-1.5"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              <IconTag /> {service.category_name || 'Service'}
            </span>
            <h2
              className="text-xl sm:text-2xl font-extrabold text-white m-0 leading-tight"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {service.name}
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 pb-6">
          {/* Stat cards */}
          <div className={`grid gap-2.5 mb-5 ${service.duration_minutes ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <div className="bg-blue-50 rounded-[14px] p-3 border border-blue-100">
              <p
                className="text-[10px] font-bold text-blue-300 tracking-[0.1em] uppercase m-0 mb-1"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                Price
              </p>
              <p className="text-lg font-extrabold text-blue-600 m-0" style={{ fontFamily: 'Sora, sans-serif' }}>
                ₱{Number(service.price).toLocaleString()}
              </p>
            </div>
            {service.duration_minutes && (
              <div className="bg-slate-50 rounded-[14px] p-3 border border-slate-200">
                <p
                  className="text-[10px] font-bold text-slate-400 tracking-[0.1em] uppercase m-0 mb-1"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  Duration
                </p>
                <p
                  className="text-lg font-extrabold text-slate-900 m-0 flex items-center gap-1"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  <IconClock /> {service.duration_minutes} min
                </p>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <p
              className="text-[10px] font-bold text-slate-400 tracking-[0.12em] uppercase mb-2"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              About this service
            </p>
            <div className="max-h-[110px] overflow-y-auto pr-1 scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-slate-300">
              <p
                className="text-[13px] text-slate-500 leading-relaxed m-0 whitespace-pre-wrap"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {service.description || 'No description provided for this service.'}
              </p>
            </div>
          </div>

          {/* Close */}
          <button
            className="w-full mt-5 inline-flex items-center justify-center font-bold text-xs tracking-widest uppercase cursor-pointer transition-colors duration-200 active:scale-95 bg-slate-900 hover:bg-blue-600 text-white py-[13px] rounded-xl border-none"
            style={{ fontFamily: 'Sora, sans-serif' }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Skeleton ───────────────────────────────────────────────────────────── */
const Skeleton = () => (
  <div className="bg-white rounded-[20px] overflow-hidden border border-slate-200">
    <div className="ms-shimmer h-[130px]" />
    <div className="p-3">
      <div className="h-[11px] rounded-md bg-slate-100 mb-2" />
      <div className="h-[11px] w-[55%] rounded-md bg-slate-100" />
    </div>
  </div>
);

/* ─── Main Page ──────────────────────────────────────────────────────────── */
const ManagerServices = () => {
  const user = useMemo(() => getStoredUser(), []);
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const searchTimeoutRef = useRef(null);

  const clearSearch = useCallback(() => { setSearchTerm(''); setDebouncedSearchTerm(''); }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/get_services.php`);
      const data = await res.json();
      if (data.success) setServices(data.services || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE}/manage_categories.php`);
      const data = await res.json();
      if (data.success) setCategories(data.categories || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchServices(); fetchCategories(); }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
    }, 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchTerm]);

  const allowedCategoryIds = useMemo(() => {
    if (!categories.length) return null;
    return categories
      .filter(cat => {
        const ids = Array.isArray(cat.branch_ids) ? cat.branch_ids : [];
        if (!ids.length) return true;
        if (!user?.branch_id) return false;
        return ids.some(id => Number(id) === Number(user.branch_id));
      })
      .map(cat => Number(cat.category_id));
  }, [categories, user?.branch_id]);

  const groupedServices = useMemo(() => {
    const query = debouncedSearchTerm.trim().toLowerCase();
    const filtered = services.filter(s =>
      s.name.toLowerCase().includes(query) ||
      (s.category_name || '').toLowerCase().includes(query)
    );
    return filtered.reduce((acc, service) => {
      if (allowedCategoryIds && !allowedCategoryIds.includes(Number(service.category_id))) return acc;
      const cat = service.category_name || 'Uncategorized';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(service);
      return acc;
    }, {});
  }, [debouncedSearchTerm, services, allowedCategoryIds]);

  const allCategoryNames = useMemo(() => Object.keys(groupedServices), [groupedServices]);

  const displayedGroups = useMemo(() => {
    if (activeCategory === 'All') return groupedServices;
    return groupedServices[activeCategory] ? { [activeCategory]: groupedServices[activeCategory] } : {};
  }, [activeCategory, groupedServices]);

  const totalDisplayed = useMemo(() =>
    Object.values(displayedGroups).reduce((a, arr) => a + arr.length, 0),
    [displayedGroups]
  );

  return (
    <div className="flex min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <ManagerSidebar />

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <ManagerTopHeader pageTitle="Service Catalog" />

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-9">

          {/* ── Page header ── */}
          <div className="mb-5">
            <p
              className="text-[10px] font-extrabold tracking-[0.2em] uppercase m-0 mb-0.5"
              style={{ fontFamily: 'Sora, sans-serif', color: '#b37b65' }}
            >
              Branch Menu
            </p>
            <h1
              className="text-2xl sm:text-3xl font-extrabold text-slate-900 m-0"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              Service Catalog
            </h1>
            {!loading && (
              <p className="text-slate-400 text-xs mt-[3px]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {services.length} services · {allCategoryNames.length} categories
              </p>
            )}
          </div>

          {/* ── Search ── */}
          <div className="relative mb-3">
            <span className="absolute left-[13px] top-1/2 -translate-y-1/2 text-slate-400 flex pointer-events-none">
              <IconSearch />
            </span>
            <input
              className="w-full py-[11px] pl-10 border-[1.5px] border-slate-200 rounded-[14px] text-sm font-medium bg-white text-slate-900 outline-none transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 placeholder:text-slate-400"
              style={{ fontFamily: 'DM Sans, sans-serif', paddingRight: searchTerm ? '40px' : '14px' }}
              placeholder="Search services…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-[11px] top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 border-none cursor-pointer flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
              >
                <IconX />
              </button>
            )}
            {isSearching && (
              <div
                className="ms-spin absolute top-1/2 -translate-y-1/2 w-[14px] h-[14px] border-2 border-slate-200 border-t-blue-600 rounded-full"
                style={{ right: searchTerm ? '44px' : '12px' }}
              />
            )}
          </div>

          {/* ── Category filter ── */}
          {!loading && allCategoryNames.length > 0 && (
            <>
              {/* Mobile: dropdown */}
              <div className="block sm:hidden relative mb-5">
                <select
                  className="appearance-none w-full py-[10px] pl-[14px] pr-10 border-[1.5px] border-slate-200 rounded-xl text-[13px] font-semibold bg-white text-slate-900 outline-none cursor-pointer transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                  value={activeCategory}
                  onChange={e => setActiveCategory(e.target.value)}
                >
                  <option value="All">All Categories ({services.length})</option>
                  {allCategoryNames.map(cat => (
                    <option key={cat} value={cat}>
                      {cat} ({groupedServices[cat]?.length || 0})
                    </option>
                  ))}
                </select>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 flex">
                  <IconChevron />
                </span>
              </div>

              {/* Desktop: pill tabs */}
              <div className="hidden sm:flex flex-wrap gap-2 mb-5">
                <button
                  className={`text-xs font-semibold px-[14px] py-1.5 rounded-full border-[1.5px] whitespace-nowrap transition-all duration-150 cursor-pointer ${
                    activeCategory === 'All'
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-slate-200 text-slate-500 hover:bg-blue-600 hover:border-blue-600 hover:text-white'
                  }`}
                  style={{ fontFamily: 'Sora, sans-serif' }}
                  onClick={() => setActiveCategory('All')}
                >
                  All <span className="opacity-65 ml-0.5">({services.length})</span>
                </button>
                {allCategoryNames.map(cat => (
                  <button
                    key={cat}
                    className={`text-xs font-semibold px-[14px] py-1.5 rounded-full border-[1.5px] whitespace-nowrap transition-all duration-150 cursor-pointer ${
                      activeCategory === cat
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-blue-600 hover:border-blue-600 hover:text-white'
                    }`}
                    style={{ fontFamily: 'Sora, sans-serif' }}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat} <span className="opacity-65 ml-0.5">({groupedServices[cat]?.length || 0})</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* ── Content ── */}
          {loading ? (
            <div>
              {[0, 1].map(si => (
                <div key={si} className="mb-9">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1 h-px bg-slate-200" />
                    <div className="h-3 w-24 rounded-md bg-slate-200" />
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                  <div className="grid gap-3 sm:gap-4 lg:gap-5 grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
                    {[0, 1, 2, 3].map(i => <Skeleton key={i} />)}
                  </div>
                </div>
              ))}
            </div>
          ) : Object.keys(displayedGroups).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-[60px] px-5 bg-white rounded-[20px] border border-dashed border-slate-200">
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" className="mb-[14px]">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <p className="font-bold text-sm text-slate-400 m-0 mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>
                No services found
              </p>
              <p className="text-slate-300 text-xs m-0 text-center">
                Try adjusting your search or selecting a different category.
              </p>
              {searchTerm && (
                <button
                  className="mt-[14px] inline-flex items-center justify-center font-bold text-[11px] tracking-widest uppercase px-5 py-[10px] rounded-[10px] border-none cursor-pointer bg-slate-900 text-white hover:bg-blue-600 transition-colors active:scale-95"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                  onClick={clearSearch}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            Object.keys(displayedGroups).map((category, catIdx) => (
              <div key={category} className="mb-9">
                {/* Section divider */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span
                    className="text-[11px] font-extrabold tracking-[0.14em] uppercase text-slate-400 whitespace-nowrap"
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {category}
                  </span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="grid gap-3 sm:gap-4 lg:gap-5 grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
                  {displayedGroups[category].map((service, idx) => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      onSelect={setSelectedService}
                      delay={catIdx * 50 + idx * 35}
                    />
                  ))}
                </div>
              </div>
            ))
          )}

          {/* Footer count */}
          {!loading && Object.keys(displayedGroups).length > 0 && (
            <p
              className="text-center text-slate-300 text-[11px] mt-2 pb-4"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              Showing {totalDisplayed} service{totalDisplayed !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>

      <ServiceModal service={selectedService} onClose={() => setSelectedService(null)} />
    </div>
  );
};

export default ManagerServices;
